import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { PublicSiteFooter } from "@/components/SiteFooter";
import VerifyEmail from "@/pages/VerifyEmail";
import PortalEntry from "@/pages/PortalEntry";
import Login from "@/pages/Login";
import Complaints from "@/pages/Complaints";
import Notifications from "@/pages/Notifications";
import Welfare from "@/pages/Welfare";
import MySouvenirsPage from "@/pages/MySouvenirsPage";
import AdminEditMember from "@/pages/AdminEditMember";
import PrintMember from "@/pages/PrintMember";
import Dashboard from "@/pages/Dashboard";
import Members from "@/pages/Members";
import Approvals from "@/pages/Approvals";
import Organization from "@/pages/Organization";
import Retirees from "@/pages/Retirees";
import ArchivedMembers from "@/pages/ArchivedMembers";
import EditRequests from "@/pages/EditRequests";
import Profile from "@/pages/Profile";
import Souvenirs from "@/pages/Souvenirs";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "member" ? "/profile" : "/dashboard"} replace />;
  }
  return children;
}

function OrgRedirect({ section }) {
  const { search } = useLocation();
  const tpl = new URLSearchParams(search).get("tab");
  return <Navigate to={`/organization?section=${section}${tpl ? `&tpl=${tpl}` : ""}`} replace />;
}

function Landing() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "member" ? "/profile" : "/dashboard"} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/join" element={<PortalEntry />} />
      <Route path="/join/:memberType" element={<PortalEntry />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Landing />} />
      <Route path="/complaints" element={<Protected><Complaints /></Protected>} />
      <Route path="/notifications" element={<Protected roles={["regional_manager", "national_manager", "super_admin"]}><Notifications /></Protected>} />
      <Route path="/templates" element={<Protected roles={["national_manager", "super_admin"]}><OrgRedirect section="templates" /></Protected>} />
      <Route path="/my-souvenirs" element={<Protected><MySouvenirsPage /></Protected>} />
      <Route path="/welfare" element={<Protected roles={["member", "facility_manager", "regional_manager", "national_manager", "super_admin"]}><Welfare /></Protected>} />
      <Route path="/dashboard" element={<Protected roles={["facility_manager", "chapter_manager", "regional_manager", "national_manager", "super_admin"]}><Dashboard /></Protected>} />
      <Route path="/members/:id/print" element={<Protected roles={["national_manager", "super_admin"]}><PrintMember /></Protected>} />
      <Route path="/members/:id/edit" element={<Protected roles={["national_manager", "super_admin"]}><AdminEditMember /></Protected>} />
      <Route path="/members" element={<Protected roles={["facility_manager", "chapter_manager", "regional_manager", "national_manager", "super_admin"]}><Members /></Protected>} />
      <Route path="/approvals" element={<Protected roles={["regional_manager", "national_manager", "super_admin"]}><Approvals /></Protected>} />
      <Route path="/edit-requests" element={<Protected roles={["national_manager", "super_admin"]}><EditRequests /></Protected>} />
      <Route path="/organization" element={<Protected roles={["national_manager", "super_admin", "regional_manager"]}><Organization /></Protected>} />
      <Route path="/staff" element={<Protected roles={["national_manager", "super_admin", "regional_manager"]}><OrgRedirect section="staff" /></Protected>} />
      <Route path="/souvenirs" element={<Protected roles={["regional_manager", "national_manager", "super_admin", "facility_manager", "chapter_manager"]}><Souvenirs /></Protected>} />
      <Route path="/retirees" element={<Protected roles={["national_manager", "super_admin"]}><Retirees /></Protected>} />
      <Route path="/archived-members" element={<Protected roles={["national_manager", "super_admin", "regional_manager"]}><ArchivedMembers /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <PublicSiteFooter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
